<template>
  <page-layout>
    <base-container fluid class="pa-5 pt-0 mt-5 pb-5 mb-5">
      <base-row>
        <CustomerRecipesListing
          :should-open-recipes-dialog="shouldOpenRecipesDialog"
          @set-should-open-recipes-dialog="setShouldOpenRecipesDialog"
        />
      </base-row>
      <base-row justify="space-between" class="pt-2 mt-5">
        <base-col v-if="totalItems > 0 || searchedText != null" cols="6">
          <base-row justify="start">
            <base-col xs="12" md="6">
              <base-text-field
                v-model="searchedText"
                density="compact"
                label="Search"
                prepend-inner-icon="mdi-magnify"
                variant="outlined"
                flat
                hide-details
                single-line
              />
            </base-col>
          </base-row>
        </base-col>
        <base-col :cols="totalItems > 0 || searchedText != null ? 6 : 12">
          <base-row justify="end">
            <base-col cols="auto"
              ><base-button color="primary w-100" @click="handleCreate"
                >Create</base-button
              ></base-col
            >
          </base-row>
        </base-col>
      </base-row>
      <base-row>
        <BaseDataTableServer
          :items="customerSegments"
          :headers="headers"
          :items-length="totalItems"
          :loading="isCustomerSegmentLoading"
          hide-actions
          :hide-default-footer="totalItems == 0 && searchedText == null"
          :hide-default-header="totalItems == 0 && searchedText == null"
          :page="pageNumber"
          :items-per-page="pageSize"
          :items-per-page-options="pageSizeOptions"
          @update:items-per-page="updatePageSize"
          @update:page="updatePageNumber"
        >
          <template #item="{ item, index }">
            <tr>
              <td class="text-left">{{ item.name }}</td>
              <td class="text-center">
                {{ item.estimatedMinAudienceCount }} -
                {{ item.estimatedMaxAudienceCount }}
              </td>
              <td class="text-center">
                {{
                  item.lastSyncedAt
                    ? getBusinessProfileDate(
                        item.lastSyncedAt,
                        bpOffset
                      ).toLocaleDateString('en-US', {
                        timeZone: 'UTC',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: 'numeric'
                      })
                    : '-'
                }}
              </td>
              <td class="text-center">
                {{
                  item.updatedAt
                    ? getBusinessProfileDate(
                        item.updatedAt,
                        bpOffset
                      ).toLocaleDateString('en-US', {
                        timeZone: 'UTC',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: 'numeric'
                      })
                    : '-'
                }}
              </td>
              <td class="text-center cursor-pointer">
                <base-row no-gutters>
                  <base-col cols="auto" @click="handleExportButton(index)">
                    <base-tooltip :text="t('message.Export')" location="bottom">
                      <template #activator="{ props }">
                        <div v-if="item.accounts" v-bind="props">
                          {{ item.accounts.length }}
                          {{ $t('message.accounts') }}
                        </div>
                        <base-icon v-else v-bind="props">
                          mdi-export-variant
                        </base-icon>
                      </template>
                    </base-tooltip>
                  </base-col>
                  <base-col
                    cols="auto"
                    class="ml-1"
                    @click="handleDelete(item)"
                  >
                    <base-tooltip
                      :text="t('messages.Delete')"
                      location="bottom"
                    >
                      <template #activator="{ props }">
                        <base-icon v-bind="props"> mdi-delete </base-icon>
                      </template>
                    </base-tooltip>
                  </base-col>
                  <base-col
                    cols="auto"
                    class="ml-1"
                    @click="handleEdit(item.id)"
                  >
                    <base-tooltip :text="t('messages.Edit')" location="bottom">
                      <template #activator="{ props }">
                        <base-icon v-bind="props"> mdi-pencil </base-icon>
                      </template>
                    </base-tooltip>
                  </base-col>
                </base-row>
              </td>
            </tr>
          </template>

          <!-- No Data Illustration -->
          <template #no-data>
            <base-row
              v-if="searchedText == null"
              no-gutters
              class="d-flex flex-column align-md-center"
            >
              <base-col class="w-md-50">
                <base-image
                  class="mx-auto"
                  height="22em"
                  width="22em"
                  :src="NoDataIllustration"
                  alt="No data illustration"
                ></base-image>
                <p>{{ $t('messages.noAudienceSegmentAvailable') }}</p>
                <p class="text-medium-emphasis mt-4">
                  <span>{{
                    $t('messages.noAudienceSegmentAvailableMessage')
                  }}</span>
                  <span
                    class="text-primary cursor-pointer text-decoration-underline ml-1"
                    @click="shouldOpenRecipesDialog = true"
                    >{{ $t('messages.selectTemplate') }}</span
                  >
                </p>
              </base-col>
            </base-row>
            <base-row v-else>
              <base-col
                ><p>{{ t('message.noDataAvailable') }}</p></base-col
              >
            </base-row>
          </template>
        </BaseDataTableServer>
      </base-row>
      <customer-segment-ad-account-dialog
        v-model="showAdAccountDialog"
        :linked-ad-account="linkedAdAccount"
        :name="segmentName"
        :segment-id="segmentId"
        @fetch-segments="fetchCustomerSegments"
      />
      <custom-modal
        v-model="showDeleteConfirmationDialog"
        width="600"
        @close="showDeleteConfirmationDialog = false"
      >
        <template #title>
          <span>{{ $t('message.delete') }}</span>
          <span>{{ ' ' + deleteSegmentDetails?.name }}</span>
        </template>
        <template #content>
          <p>{{ $t('messages.deleteSegmentConfirmation') }}</p>
        </template>
        <template #action-button>
          <base-button
            class="mr-2"
            @click="showDeleteConfirmationDialog = false"
            >{{ $t('message.cancel') }}</base-button
          >
          <base-button color="primary" class="mr-2" @click="deleteSegment">{{
            $t('message.delete')
          }}</base-button>
        </template>
      </custom-modal>
    </base-container>
  </page-layout>
</template>

<script lang="ts" setup>
  import { getBusinessProfileDate } from '@/helpers/dateHelper'
  import { ref, computed, onMounted, watch } from 'vue'
  import { useRouter } from 'vue-router'
  import { useAppStore } from '@/store/app'
  import useAudienceStore from '@/store/audience'
  import {
    BaseTooltip,
    BaseTextField,
    BaseRow,
    BaseCol,
    BaseContainer,
    BaseIcon,
    BaseButton,
    BaseDataTableServer,
    BaseImage
  } from '@deltax/vuetify-wrapper-components'
  import CustomModal from '@/components/customComponents/CustomModal.vue'
  import CustomerSegmentAdAccountDialog from './Components/CustomerSegmentAdAccountDialog.vue'
  import CustomerRecipesListing from '../AudienceCreation/Components/CustomerRecipesListing.vue'
  import NoDataIllustration from '../../../public/assets/static/images/noDataNew.svg'
  import { channels } from '@/constants/Channel'
  import {
    BpseSegmentInfo,
    CustomerSegmentResonse
  } from '@/types/customerSegmentResponse'
  import SegmentService from '@/services/SegmentService'
  import AccountService from '@/services/accountService'
  import NotifyError from '@/utils/NotifyError'
  import PageLayout from '@/layouts/PageLayout.vue'
  import { debounce } from 'lodash'
  import { useI18n } from 'vue-i18n'
  import pageSizeOptions from '@/constants/itemsPerPage'
  import { useNotificationStore } from '@/store/notification'
  import customerSegmentStatus from '@/constants/LeadX/customerSegmentStatus'
  import editAudienceRoute from '../EditAudience/editAudienceRoute'

  const { t } = useI18n()
  // constants
  const headers = [
    { title: 'Name', key: 'name', align: 'left', sortable: false },
    { title: 'Size', key: 'size', align: 'center', sortable: false },
    {
      title: 'Last synced',
      key: 'lastSyncedAt',
      align: 'center',
      sortable: false
    },
    {
      title: 'Last updated',
      key: 'lastUpdated',
      align: 'center',
      sortable: false
    },
    { title: 'Action', key: 'action', align: 'start', sortable: false }
  ]

  // Stores and Services
  const appStore = useAppStore()
  const bpOffset = computed(
    () => appStore.getSelectedBusinessProfileTimezone?.offset ?? 0
  )
  const audienceStore = useAudienceStore()
  const router = useRouter()
  const segmentService = new SegmentService()
  const accountService = new AccountService()
  const notification = useNotificationStore()

  const shouldOpenRecipesDialog = ref(false)
  const setShouldOpenRecipesDialog = (value: boolean) => {
    shouldOpenRecipesDialog.value = value
  }

  const searchedText = ref<string | null>(null)
  watch(
    searchedText,
    debounce(async () => {
      await fetchCustomerSegments()
    }, 300)
  )

  const customerSegments = ref<CustomerSegmentResonse[]>([])

  const pageSize = ref(10)
  const pageNumber = ref(1)
  const totalItems = ref(0)

  watch(pageSize, async () => {
    await fetchCustomerSegments()
  })

  watch(pageNumber, async () => {
    await fetchCustomerSegments()
  })

  const updatePageNumber = (newPageNumber: number) => {
    pageNumber.value = newPageNumber
  }

  const updatePageSize = (newItemsPerPage: number) => {
    pageSize.value = newItemsPerPage
  }

  const isCustomerSegmentLoading = ref(false)
  const fetchCustomerSegments = async () => {
    try {
      isCustomerSegmentLoading.value = true
      if (appStore.getSelectedBusinessProfile) {
        const leadXOrganisationDetails = await accountService.getAccountDetails(
          appStore.getSelectedBusinessProfile.id,
          channels.LeadX
        )
        const customerSegmentResponse = await segmentService.getCustomerSegment(
          appStore.getSelectedBusinessProfile.id,
          leadXOrganisationDetails[0].id,
          searchedText.value,
          pageNumber.value,
          pageSize.value,
          '' +
            customerSegmentStatus.Active +
            ',' +
            customerSegmentStatus.InProcess
        )
        customerSegments.value = customerSegmentResponse.data
        totalItems.value = customerSegmentResponse.totalRecords
      }
    } catch (error) {
      NotifyError(error)
    } finally {
      isCustomerSegmentLoading.value = false
    }
  }

  onMounted(async () => {
    await fetchCustomerSegments()
  })

  const showAdAccountDialog = ref(false)
  const segmentName = ref('')
  const segmentId = ref(0)
  const linkedAdAccount = ref<BpseSegmentInfo[]>([])
  const handleExportButton = (index: number) => {
    showAdAccountDialog.value = true
    if (customerSegments.value[index].accounts) {
      linkedAdAccount.value = customerSegments.value[index].accounts
    } else {
      linkedAdAccount.value = []
    }
    segmentName.value = customerSegments.value[index].name
    segmentId.value = customerSegments.value[index].id
  }

  const handleCreate = () => {
    router.push({ name: 'audienceCreation' })
  }

  const showDeleteConfirmationDialog = ref(false)
  const deleteSegmentDetails = ref<CustomerSegmentResonse | null>(null)
  const deleteSegment = async () => {
    try {
      if (appStore.getSelectedBusinessProfile && deleteSegmentDetails.value) {
        segmentService.deleteSegment(
          appStore.getSelectedBusinessProfile.id,
          deleteSegmentDetails.value.id
        )
      }
      showDeleteConfirmationDialog.value = false
      notification.notify(
        t('messages.CustomerSegmentDeletedSuccessMessage'),
        'success'
      )
      // make the recipe details null, so that the recipes details fetch again
      audienceStore.resetRecipeDetails()
      await fetchCustomerSegments()
    } catch (error: Error) {
      showDeleteConfirmationDialog.value = false
      notification.notify(t('errors.somethingWentWrong'), 'error')
      NotifyError(error)
    }
  }

  const handleDelete = async (segment: CustomerSegmentResonse) => {
    deleteSegmentDetails.value = segment
    showDeleteConfirmationDialog.value = true
  }

  const handleEdit = async (audienceId: number) => {
    router.push({
      name: editAudienceRoute.name,
      params: { audienceId: audienceId }
    })
  }
</script>
